'use strict';

// EPROC-10693: Autosave fuer interne Notizen pro Warenkorb-Position.
// Pro <textarea data-basketitem-note-autosave="1" data-basketitem-id="<id>">
// wird der Wert beim Tippen debounced an basket/basket_save_note.htm geschickt.
// Andere Form-Felder bleiben unveraendert: "Aenderungen uebernehmen" persistiert sie weiterhin.

const SELECTOR = 'textarea[data-basketitem-note-autosave="1"]';
const ENDPOINT_HTML = 'basket/basket_action.htm';
const ENDPOINT_ACTION = 'SAVE_INTERNAL_NOTE';
const DEBOUNCE_MS = 600;
const SAVED_HIDE_MS = 1500;

const REGISTRY = new WeakMap();

class NoteAutosaver {
    constructor(textarea) {
        this.el = textarea;
        this.itemId = textarea.dataset.basketitemId;
        this.lastSent = textarea.value;
        this.debounceTimer = null;
        this.inflight = null;
        this.queuedValue = null;
        this.indicator = this._createIndicator();
        this._onInput = () => this._scheduleSave();
        this._onBlur = () => this._flushNow();
        this.el.addEventListener('input', this._onInput);
        this.el.addEventListener('blur', this._onBlur);
    }

    _createIndicator() {
        const span = document.createElement('span');
        span.className = 'basketitem-note-autosave-status text-muted small';
        span.style.display = 'inline-block';
        span.style.marginTop = '2px';
        span.style.minHeight = '1em';
        if (this.el.parentNode) {
            this.el.parentNode.insertBefore(span, this.el.nextSibling);
        }
        return span;
    }

    _scheduleSave() {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this._flushNow(), DEBOUNCE_MS);
    }

    _flushNow() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        const value = this.el.value;
        if (value === this.lastSent) return;

        if (this.inflight) {
            this.queuedValue = value;
            return;
        }
        this._dispatch(value);
    }

    _dispatch(value) {
        const form = this.el.closest('form');
        if (!form) {
            this._setStatus('error');
            return;
        }
        const ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
        this.inflight = ctrl;
        this._setStatus('saving');

        const url = form.getAttribute('action');
        const body = this._buildBody(form, value);

        fetch(url, {
            method: 'POST',
            body,
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
            signal: ctrl ? ctrl.signal : undefined,
        }).then((r) => r.text().then((text) => ({ status: r.status, text }))).then(({ status, text }) => {
            const data = parseJsonLoose(text);
            this.inflight = null;
            const result = interpretActionResponse(status, data);
            if (result.ok) {
                this.lastSent = value;
                this._setStatus('saved');
            } else {
                console.warn('[basket-note-autosave] save failed', { status, text: text && text.slice(0, 500), data });
                this._setStatus('error', result.detail);
            }
            this._drainQueue();
        }).catch((err) => {
            this.inflight = null;
            if (err && err.name === 'AbortError') {
                this._setStatus('idle');
                return;
            }
            console.warn('[basket-note-autosave] network error', err);
            this._setStatus('error', 'network');
        });
    }

    _drainQueue() {
        if (this.queuedValue === null) return;
        const next = this.queuedValue;
        this.queuedValue = null;
        if (next !== this.lastSent) this._dispatch(next);
    }

    _buildBody(form, value) {
        const params = new URLSearchParams();
        params.append('HTML', ENDPOINT_HTML);
        params.append('ACTION', ENDPOINT_ACTION);
        params.append('VS_BASKETITEMS_ID', this.itemId);
        params.append('VS_NOTE', value);
        const checkid = form.querySelector('input[name="CHECKID"]');
        if (checkid) params.append('CHECKID', checkid.value);
        const sid = form.querySelector('input[name="ID"]');
        if (sid) params.append('ID', sid.value);
        return params.toString();
    }

    _setStatus(state, detail) {
        const i = this.indicator;
        if (!i) return;
        switch (state) {
            case 'saving':
                i.textContent = 'Speichert…';
                i.className = 'basketitem-note-autosave-status text-muted small';
                break;
            case 'saved':
                i.textContent = '✓ Gespeichert';
                i.className = 'basketitem-note-autosave-status text-success small';
                if (this._hideTimer) clearTimeout(this._hideTimer);
                this._hideTimer = setTimeout(() => {
                    if (i.textContent === '✓ Gespeichert') i.textContent = '';
                }, SAVED_HIDE_MS);
                break;
            case 'error':
                i.textContent = '⚠ Fehler beim Speichern' + (detail ? ' (' + detail + ')' : '');
                i.className = 'basketitem-note-autosave-status text-danger small';
                break;
            case 'idle':
            default:
                i.textContent = '';
                break;
        }
    }

    cancel() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        this.queuedValue = null;
        if (this.inflight) {
            this.inflight.abort();
            this.inflight = null;
        }
    }

    sendBeaconFlush() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
        const value = this.el.value;
        if (value === this.lastSent) return;
        if (!navigator.sendBeacon) return;
        const form = this.el.closest('form');
        if (!form) return;
        const body = this._buildBody(form, value);
        const blob = new Blob([body], { type: 'application/x-www-form-urlencoded' });
        if (navigator.sendBeacon(form.getAttribute('action'), blob)) {
            this.lastSent = value;
        }
    }
}

function interpretActionResponse(status, data) {
    // basket_action.htm liefert {"ret":[{status:"success"|"danger"|...,"message":"..."}]}.
    // Erfolg: HTTP 200 + ret-Array vorhanden + keine Fehler-Eintraege.
    if (status !== 200) return { ok: false, detail: 'HTTP ' + status };
    if (!data || !Array.isArray(data.ret)) return { ok: false, detail: 'no_json' };
    const failure = data.ret.find((m) => {
        const s = m && m.status;
        return s === 'danger' || s === 'error' || s === 'fail' || s === '1' || s === 1;
    });
    if (failure) return { ok: false, detail: failure.message || 'failed' };
    return { ok: true };
}

function parseJsonLoose(text) {
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch (e) {
        const m = text.match(/\{[\s\S]*\}/);
        if (!m) return null;
        try { return JSON.parse(m[0]); } catch (e2) { return null; }
    }
}

function attach(textarea) {
    if (REGISTRY.has(textarea)) return;
    if (!textarea.dataset.basketitemId) return;
    REGISTRY.set(textarea, new NoteAutosaver(textarea));
}

function scan(root) {
    const r = root || document;
    if (!r.querySelectorAll) return;
    r.querySelectorAll(SELECTOR).forEach(attach);
}

function forEachInstance(fn) {
    document.querySelectorAll(SELECTOR).forEach((t) => {
        const inst = REGISTRY.get(t);
        if (inst) fn(inst);
    });
}

function wrapSendForm() {
    if (typeof window.sendForm !== 'function' || window.sendForm.__autosaveWrapped) return;
    const orig = window.sendForm;
    const wrapped = function (...args) {
        // Stale Autosaves abbrechen, damit sie nicht den frischen UPDATE-Wert ueberschreiben.
        forEachInstance((i) => i.cancel());
        return orig.apply(this, args);
    };
    wrapped.__autosaveWrapped = true;
    window.sendForm = wrapped;
}

function init() {
    scan(document);

    // Loaders.js (mit sendForm) wird nach main.js geladen - deshalb erst nach window.load wrappen.
    if (document.readyState === 'complete') {
        wrapSendForm();
    } else {
        window.addEventListener('load', wrapSendForm);
    }

    window.addEventListener('pagehide', () => forEachInstance((i) => i.sendBeaconFlush()));

    // Basket-Page wird via setContent dynamisch eingehaengt - neue Textareas erkennen.
    const obs = new MutationObserver((muts) => {
        muts.forEach((m) => {
            m.addedNodes.forEach((n) => {
                if (n.nodeType !== 1) return;
                if (n.matches && n.matches(SELECTOR)) attach(n);
                else scan(n);
            });
        });
    });
    obs.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
